import React, { useCallback } from 'react'

import { Icon, palette } from '@centivo/react-components'
import { v4 } from 'uuid'

import { Spinner } from 'src/refactor/components'
import { IPractice } from 'src/shared/types'
import { PhoneNumber } from 'src/refactor/components/IconTextComponent'
import { GetProvidersFilter } from 'src/api/types'

import { useResultsState } from 'src/context/results'
import { useSliceTemplate } from 'src/context/global'
import { ProviderResultListItemPractitionerItem } from './ProviderResultListItemPractitionerItem'
import {
  PorlivpButton,
  PorlivpContainer,
} from './ProviderResultListItemViewProviders.styled'

interface ViewProps
  extends Pick<
    IPractice,
    'network' | 'lat' | 'long' | 'practiceName' | 'practicePhone' | 'distance'
  > {
  id: string
  onClickExpand(id: string | null): void
  filterOptions: GetProvidersFilter
  specialtyCode?: string
  onExtend?(): void
  isOpen?: boolean
  isVirtualCareOnly?: boolean
}

export const ProviderResultListItemViewProviders: React.FC<ViewProps> = ({
  id,
  practicePhone,
  distance,
  lat,
  long,
  practiceName,
  specialtyCode,
  isOpen,
  onClickExpand,
  filterOptions,
  isVirtualCareOnly,
}) => {
  const [state, actions] = useResultsState()
  const { viewPractitionerList, isPractitionerListLoading } = state

  const practitioners = viewPractitionerList?.find(
    el => el.practice === id
  )?.list

  const onClickOpen = useCallback(() => {
    onClickExpand(isOpen ? null : id)

    if (!isOpen) {
      actions.getProvidersByPractice({
        search: {
          serviceLines: state.search.serviceLines,
          placeId: state.search.placeId,
          practiceId: id,
          addressLabel: state.search.addressLabel,
        },
        filter: {
          ...filterOptions,
        },
      })
    }
  }, [
    id,
    isOpen,
    actions,
    lat,
    long,
    practiceName,
    specialtyCode,
    filterOptions,
    onClickExpand,
    isVirtualCareOnly,
    state.filter.withVirtualCare,
  ])

  const isEmpty = !practitioners || practitioners.length === 0
  const isSliceTemplate = useSliceTemplate()

  const spinnerColor = palette.brand.orange

  const icon = {
    minus: isSliceTemplate ? 'slice-minus' : 'minus',
    plus: isSliceTemplate ? 'slice-plus' : 'plus',
  } as const

  return (
    <PorlivpContainer>
      <PorlivpButton
        isOpen={isOpen}
        onClick={onClickOpen}
        aria-label={`Tap to ${
          isOpen ? 'close the list' : 'open a list'
        } of providers available on ${practiceName}`}
        aria-expanded={isOpen}
      >
        <span>View providers</span>
        <Icon
          icon={isOpen ? icon.minus : icon.plus}
          size="1.2rem"
          aria-hidden
        />
      </PorlivpButton>
      {!isSliceTemplate && isOpen && practicePhone ? (
        <PhoneNumber number={practicePhone} marginBottom="0.8rem" aria-hidden />
      ) : null}
      {isOpen && isPractitionerListLoading ? (
        <Spinner color={spinnerColor} size="25px" />
      ) : null}
      {isOpen &&
        !isPractitionerListLoading &&
        (isEmpty ? (
          <p>No provider found, try changing the filters</p>
        ) : (
          <ul>
            {practitioners.map(practitioner => (
              <li>
                <ProviderResultListItemPractitionerItem
                  {...practitioner}
                  distance={distance}
                  key={v4()}
                />
              </li>
            ))}
          </ul>
        ))}
    </PorlivpContainer>
  )
}
